import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import {
  AutocompleteInput,
  ReferenceInput,
  TextInput,
  required,
  useRecordContext,
} from "react-admin";
import { useFormContext, useWatch } from "react-hook-form";

import NoArrowKeyNumberInput from "@/components/NoArrowKeyNumberInput";
import { formatINR } from "@/utils";

import { ProjectUnitAmountProvider, useProjectUnitAmount } from "./project-unit-amount-context";
import {
  validateProjectTakhmeenCommittedAmountField,
  validateProjectTakhmeenCommittedNotBelowPaid,
  validateProjectTakhmeenUnitsField,
} from "./project-takhmeen-form-transforms";

type ProjectTakhmeenFormFieldsProps = {
  routeProjectId?: string | null;
  mode: "create" | "edit";
};

export function ProjectTakhmeenFormFields({
  routeProjectId,
  mode,
}: ProjectTakhmeenFormFieldsProps) {
  return (
    <ProjectUnitAmountProvider
      routeProjectId={routeProjectId}
      skipInitialCommittedSeed={mode === "edit"}
    >
      <ProjectTakhmeenFormFieldsInner routeProjectId={routeProjectId} mode={mode} />
    </ProjectUnitAmountProvider>
  );
}

function ProjectTakhmeenFormFieldsInner({ routeProjectId, mode }: ProjectTakhmeenFormFieldsProps) {
  const record = useRecordContext();
  const paidAmount = record?.paidAmount != null ? Number(record.paidAmount) : 0;
  const amountsLocked = mode === "edit" && paidAmount > 0;

  return (
    <Grid container spacing={2}>
      {mode === "create" && !routeProjectId ? (
        <Grid size={12}>
          <ReferenceInput source="projectId" reference="projects" perPage={100}>
            <AutocompleteInput optionText="name" label="Project" validate={required()} fullWidth />
          </ReferenceInput>
        </Grid>
      ) : null}

      {mode === "create" ? (
        <Grid size={12}>
          <ReferenceInput source="itsdataId" reference="itsdata" perPage={25}>
            <AutocompleteInput
              optionText={(r: { Full_Name?: string; ITS_ID?: string }) =>
                `${r.Full_Name ?? ""} (${r.ITS_ID ?? ""})`.trim()
              }
              label="ITS"
              validate={required()}
              fullWidth
            />
          </ReferenceInput>
        </Grid>
      ) : null}

      <Grid size={{ xs: 12, sm: 4 }}>
        <NoArrowKeyNumberInput
          source="units"
          label="Units"
          fullWidth
          validate={(value) => {
            if (value === "" || value == null) return "Required";
            return validateProjectTakhmeenUnitsField(value);
          }}
          disabled={amountsLocked}
          helperText={
            amountsLocked ? "Cannot change after receipts exist" : "Number of pledge units"
          }
        />
      </Grid>

      <Grid size={{ xs: 12, sm: 4 }}>
        <ProjectUnitAmountPreview />
      </Grid>

      <Grid size={{ xs: 12, sm: 4 }}>
        <CalculatedCommittedPreview />
      </Grid>

      <Grid size={{ xs: 12, sm: 6 }}>
        <CommittedAmountInput mode={mode} paidAmount={paidAmount} amountsLocked={amountsLocked} />
      </Grid>

      {mode === "edit" && paidAmount > 0 ? (
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            label="Paid amount (₹)"
            value={formatINR(paidAmount, { empty: "—" })}
            fullWidth
            disabled
          />
        </Grid>
      ) : null}

      <Grid size={12}>
        <TextInput source="remarks" multiline fullWidth minRows={3} />
      </Grid>
    </Grid>
  );
}

function CommittedAmountInput({
  mode,
  paidAmount,
  amountsLocked,
}: {
  mode: "create" | "edit";
  paidAmount: number;
  amountsLocked: boolean;
}) {
  const { markCommittedManuallyEdited, unitAmount } = useProjectUnitAmount();
  const { control } = useFormContext();
  const units = useWatch({ control, name: "units" });
  const committedAmount = useWatch({ control, name: "committedAmount" });

  const u = Number(units);
  const calculated =
    unitAmount != null && Number.isFinite(u) && u >= 0 ? Math.round(u * unitAmount) : undefined;
  const committed = Number(committedAmount);
  const differsFromDefault =
    calculated != null && Number.isFinite(committed) && committed !== calculated;

  let helperText = amountsLocked
    ? "Cannot change after receipts exist"
    : "Defaults to units × unit amount; edit to pledge a different total";
  if (!amountsLocked && differsFromDefault) {
    helperText = `Differs from calculated default (${formatINR(calculated, { empty: "—" })})`;
  }

  return (
    <NoArrowKeyNumberInput
      source="committedAmount"
      label="Committed amount (₹)"
      fullWidth
      validate={[
        validateProjectTakhmeenCommittedAmountField,
        ...(mode === "edit" ? [validateProjectTakhmeenCommittedNotBelowPaid(paidAmount)] : []),
      ]}
      disabled={amountsLocked}
      helperText={helperText}
      onChange={() => {
        markCommittedManuallyEdited();
      }}
    />
  );
}

function ProjectUnitAmountPreview() {
  const { unitAmount, isLoading, projectId } = useProjectUnitAmount();

  let helper: string | undefined;
  if (!projectId) {
    helper = "Select a project first";
  } else if (isLoading) {
    helper = "Loading…";
  }

  return (
    <TextField
      label="Project unit amount (₹)"
      value={unitAmount != null ? formatINR(unitAmount, { empty: "—" }) : "—"}
      fullWidth
      disabled
      helperText={helper}
    />
  );
}

function CalculatedCommittedPreview() {
  const { unitAmount } = useProjectUnitAmount();
  const { control } = useFormContext();
  const units = useWatch({ control, name: "units" });

  const u = Number(units);
  const calculated =
    unitAmount != null && Number.isFinite(u) && u >= 0 ? Math.round(u * unitAmount) : undefined;

  return (
    <TextField
      label="Calculated default (₹)"
      value={calculated != null ? formatINR(calculated, { empty: "—" }) : "—"}
      fullWidth
      disabled
      helperText="units × unit amount"
    />
  );
}
